import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/propgen/Sidebar";
import { api, formatApiError } from "@/lib/propgen_api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowUpRight, Search, Sparkles, Users } from "lucide-react";

const statusPill = (s) => {
  const map = {
    draft:     "bg-neutral-200 text-neutral-800",
    extracted: "bg-blue-100 text-blue-800 border-blue-800",
    estimated: "bg-amber-100 text-amber-900 border-amber-800",
    drafted:   "bg-emerald-100 text-emerald-900 border-emerald-800",
    approved:  "bg-black text-white",
  };
  return `inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${map[s] || map.draft}`;
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const seedDemos = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/projects/seed-demo");
      if (data.seeded) {
        toast.success(`Seeded ${data.seeded} demo project${data.seeded === 1 ? "" : "s"}`);
      } else {
        toast.info(data.message || "Demo projects already seeded");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const filtered = projects.filter((p) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.client_name || "").toLowerCase().includes(q);
  });

  const stats = {
    total: projects.length,
    drafted: projects.filter((p) => p.status === "drafted").length,
    estimated: projects.filter((p) => p.status === "estimated").length,
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="dashboard-page">
      <Sidebar onNewProject={() => setShowNew(true)} />

      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="border-b bg-card">
          <div className="px-8 py-6 flex items-end justify-between gap-6">
            <div>
              <div className="eyebrow">Workspace / Projects</div>
              <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Proposal projects</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search project or client…"
                  data-testid="dashboard-search-input"
                  className="pl-9 h-10 w-72 border-2 font-mono text-xs"
                />
              </div>
              <Button
                onClick={seedDemos}
                disabled={seeding}
                data-testid="propgen-seed-demos-btn"
                className="h-10 bg-[var(--peak-ink)] hover:bg-[var(--peak-blue)] text-white font-display font-bold tracking-wide uppercase text-xs"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {seeding ? "Seeding…" : "Seed demos"}
              </Button>
            </div>
          </div>
          {/* KPI strip */}
          <div className="px-8 pb-6 grid grid-cols-1 sm:grid-cols-3 border-t bg-background">
            {[
              { label: "Total Projects", value: stats.total, testid: "stat-total" },
              { label: "Estimated", value: stats.estimated, testid: "stat-estimated" },
              { label: "Drafted", value: stats.drafted, testid: "stat-drafted" },
            ].map((s, i) => (
              <div key={s.label} className={`py-5 px-6 ${i < 2 ? "border-r" : ""}`} data-testid={s.testid}>
                <div className="eyebrow">{s.label}</div>
                <div className="font-display text-4xl font-black tracking-tighter mt-1">{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Project table */}
        <div className="p-8">
          <div className="border bg-card" data-testid="projects-table">
            <div className="grid grid-cols-[1fr_1fr_140px_140px_60px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              <div>Project</div>
              <div>Client</div>
              <div>Status</div>
              <div>Updated</div>
              <div></div>
            </div>
            {loading ? (
              <div className="p-8 text-sm text-muted-foreground" data-testid="loading-state">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-16 flex flex-col items-center text-center gap-3" data-testid="empty-state">
                <div className="w-14 h-14 border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                  <Users className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="font-display text-lg font-bold">No projects yet</div>
                <div className="text-sm text-muted-foreground max-w-sm">
                  Create your first proposal project. Upload a discovery-call transcript and we&apos;ll do the rest.
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    onClick={() => setShowNew(true)}
                    data-testid="empty-new-project-btn"
                    className="bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold tracking-wide uppercase text-xs"
                  >
                    Create Project →
                  </Button>
                  <Button
                    onClick={seedDemos}
                    disabled={seeding}
                    data-testid="empty-seed-demos-btn"
                    variant="outline"
                    className="border-2 border-[var(--peak-ink)] text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white font-display font-bold tracking-wide uppercase text-xs"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {seeding ? "Seeding…" : "Seed demos"}
                  </Button>
                </div>
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => nav(`/superadmin/propgen/projects/${p.id}`)}
                  data-testid={`project-row-${p.id}`}
                  className="w-full grid grid-cols-[1fr_1fr_140px_140px_60px] items-center px-4 py-3 border-b last:border-b-0 hover:bg-muted/40 transition-colors duration-150 text-left"
                >
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-sm text-muted-foreground truncate">{p.client_name}</div>
                  <div><span className={statusPill(p.status)}>{p.status}</span></div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                  </div>
                  <div className="flex justify-end"><ArrowUpRight className="w-4 h-4 text-muted-foreground" /></div>
                </button>
              ))
            )}
          </div>
        </div>
      </main>

      <NewProjectDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); nav(`/superadmin/propgen/projects/${p.id}`); }} />
    </div>
  );
}

function NewProjectDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [peakContactId, setPeakContactId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);

  // Autocomplete from Peak's own contacts collection (not PropGen's namespaced
  // one) — we call `${BACKEND}/api/contacts` directly with credentials.
  useEffect(() => {
    if (!open) return;
    const q = client.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const ctrl = new AbortController();
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/contacts?q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: "include", signal: ctrl.signal });
        if (!res.ok) return;
        const rows = await res.json();
        setSuggestions(Array.isArray(rows) ? rows.slice(0, 8) : []);
      } catch (_) { /* ignored */ }
    })();
    return () => ctrl.abort();
  }, [client, open]);

  const pickContact = (c) => {
    const displayName = c.company || c.name || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;
    setClient(displayName);
    setPeakContactId(c.id);
    setShowSug(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !client.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/projects", {
        name,
        client_name: client,
        description: desc,
        peak_contact_id: peakContactId,
      });
      toast.success("Project created");
      onCreated(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">New Proposal Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="eyebrow block mb-1.5">Project Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme AI Platform" data-testid="new-project-name-input" className="h-10 border-2" />
          </div>
          <div className="relative">
            <label className="eyebrow block mb-1.5">
              Client
              {peakContactId && <span className="ml-2 text-[10px] font-mono text-[var(--peak-accent)]">· LINKED TO PEAK CONTACT</span>}
            </label>
            <Input
              value={client}
              onChange={(e) => { setClient(e.target.value); setPeakContactId(null); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 200)}
              placeholder="Acme Corp."
              data-testid="new-project-client-input"
              className="h-10 border-2"
              autoComplete="off"
            />
            {showSug && suggestions.length > 0 && (
              <div
                className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-[var(--peak-ink)] max-h-56 overflow-auto"
                data-testid="client-autocomplete"
              >
                {suggestions.map((c) => {
                  const person = c.name || [c.first_name, c.last_name].filter(Boolean).join(" ");
                  const primary = c.company || person || c.email;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickContact(c); }}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--peak-bg)] border-b border-[var(--peak-border)] last:border-b-0"
                      data-testid={`client-suggestion-${c.id}`}
                    >
                      <div className="font-semibold text-sm">{primary}</div>
                      <div className="text-xs text-[var(--peak-muted)] font-mono">
                        {person && person !== primary ? person : ""}{c.email ? ` · ${c.email}` : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Short Description (optional)</label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="new-project-desc-input" className="border-2" />
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="h-10 px-4 text-sm border-2 hover:bg-muted transition-colors duration-150" data-testid="new-project-cancel">Cancel</button>
            <Button type="submit" disabled={loading || !name.trim() || !client.trim()} data-testid="new-project-submit" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide">
              {loading ? "Creating…" : "Create Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
